(() => {
  'use strict';

  if (window.TanatelContactMap) {
    window.TanatelContactMap.init(document);
    return;
  }

  const SELECTOR = '[data-contact-map]';
  const MOBILE_QUERY = '(max-width: 990px)';
  const EARTH_RADIUS_KM = 6371;

  const toRadians = (value) => value * Math.PI / 180;

  const distanceKm = (lat1, lng1, lat2, lng2) => {
    const deltaLat = toRadians(lat2 - lat1);
    const deltaLng = toRadians(lng2 - lng1);
    const a =
      Math.sin(deltaLat / 2) ** 2 +
      Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(deltaLng / 2) ** 2;

    return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const initSection = (section) => {
    if (!(section instanceof HTMLElement) || section.dataset.mapSwitcherReady === 'true') return;

    const frame = section.querySelector('[data-branch-map-frame]');
    const list = section.querySelector('[data-branch-list]');
    const nearestButton = section.querySelector('[data-find-nearest]');
    const nearestLabel = section.querySelector('[data-nearest-label]');
    const status = section.querySelector('[data-nearest-status]');
    const mapCard = section.querySelector('.tanatel-contact-map__map-card');
    let cards = Array.from(section.querySelectorAll('[data-static-branch-card]'));

    if (!frame || !list || !cards.length) return;

    section.dataset.mapSwitcherReady = 'true';
    const originalNearestLabel = nearestLabel?.textContent?.trim() || '';
    const regionGroups = Array.from(section.querySelectorAll('[data-region-group]'));

    regionGroups.forEach((region) => {
      region.addEventListener('toggle', () => {
        if (!region.open || !window.matchMedia(MOBILE_QUERY).matches) return;
        regionGroups.forEach((otherRegion) => {
          if (otherRegion !== region) otherRegion.open = false;
        });
      });
    });

    const activate = (card, trigger, shouldScroll = true) => {
      const source = trigger?.dataset.mapSrc;
      if (!source) return;

      cards.forEach((item) => item.classList.toggle('is-active', item === card));

      if (frame.getAttribute('src') !== source) {
        frame.setAttribute('src', source);
      }

      frame.title = trigger.dataset.mapTitle || frame.title;

      if (shouldScroll && window.matchMedia(MOBILE_QUERY).matches) {
        mapCard?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    list.addEventListener('click', (event) => {
      const card = event.target.closest('[data-static-branch-card]');
      if (!card || !list.contains(card)) return;

      const mapTrigger = card.querySelector('[data-switch-branch-map]');
      if (!mapTrigger) return;

      const clickedMapTrigger = event.target.closest('[data-switch-branch-map]');
      if (clickedMapTrigger) {
        event.preventDefault();
        activate(card, clickedMapTrigger);
        return;
      }

      const interactiveElement = event.target.closest(
        'a, button, input, select, textarea, label, summary, [role="button"]'
      );

      if (interactiveElement) return;

      activate(card, mapTrigger);
    });

    list.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (event.target.closest('a, button')) return;

      const card = event.target.closest('[data-static-branch-card]');
      const trigger = card?.querySelector('[data-switch-branch-map]');
      if (!card || !trigger) return;

      event.preventDefault();
      activate(card, trigger);
    });

    nearestButton?.addEventListener('click', () => {
      if (!status || !nearestLabel || !navigator.geolocation) {
        if (status) status.textContent = status.dataset.error || '';
        return;
      }

      nearestButton.disabled = true;
      nearestLabel.textContent = status.dataset.loading || originalNearestLabel;
      status.textContent = '';

      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          const openCards = cards.filter(
            (card) =>
              card.dataset.branchClosed !== 'true' &&
              card.dataset.branchComingSoon !== 'true'
          );

          const ranked = openCards
            .map((card) => ({
              card,
              distance: distanceKm(
                coords.latitude,
                coords.longitude,
                Number(card.dataset.branchLat),
                Number(card.dataset.branchLng)
              )
            }))
            .filter(({ distance }) => Number.isFinite(distance))
            .sort((a, b) => a.distance - b.distance);

          if (!ranked.length) {
            status.textContent = status.dataset.error || '';
            nearestButton.disabled = false;
            nearestLabel.textContent = originalNearestLabel;
            return;
          }

          cards.forEach((card) => {
            card.classList.remove('is-nearest');
            delete card.dataset.distance;
          });

          ranked.forEach(({ card, distance }, index) => {
            card.classList.toggle('is-nearest', index === 0);
            card.dataset.distance = distance.toFixed(1);
          });

          const first = ranked[0].card;
          const firstRegion = first.closest('[data-region-group]');

          if (firstRegion) {
            firstRegion.open = true;
            if (window.matchMedia(MOBILE_QUERY).matches) {
              regionGroups.forEach((region) => {
                if (region !== firstRegion) region.open = false;
              });
            }
          }

          const firstName = first.querySelector('h3')?.textContent?.trim() || '';
          status.textContent = `${status.dataset.success || ''}: ${firstName} — ${first.dataset.distance} km`;
          activate(first, first.querySelector('[data-switch-branch-map]'), false);

          window.requestAnimationFrame(() => {
            first.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          });

          nearestButton.disabled = false;
          nearestLabel.textContent = originalNearestLabel;
        },
        (error) => {
          nearestButton.disabled = false;
          nearestLabel.textContent = originalNearestLabel;
          status.textContent = error.code === error.PERMISSION_DENIED
            ? (status.dataset.denied || '')
            : (status.dataset.error || '');
        },
        {
          enableHighAccuracy: false,
          timeout: 8000,
          maximumAge: 300000
        }
      );
    });
  };

  const init = (root = document) => {
    if (root.matches?.(SELECTOR)) initSection(root);
    root.querySelectorAll?.(SELECTOR).forEach(initSection);
  };

  window.TanatelContactMap = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
